import { Skeleton } from '@tempo-io/sui-skeleton';
import { useTranslation } from 'react-i18next';
import { computeEffectiveAmount, Row, Section } from './financialPanelLayout';
import { SavableUnitField } from './SavableUnitField';
import type { FinancialSaveState, ItemFinancialFigures } from './types';
import { MAX_MONETARY_AMOUNT } from './types';
import { useLiveDraft } from './useLiveDraft';

interface RevenueSectionProps {
  figures?: ItemFinancialFigures;
  symbol: string;
  money: (value: number | null | undefined) => string;
  isReadOnly: boolean;
  isLoading?: boolean;
  isLowestLevel?: boolean;
  onForecastedRevenueChange?: (value: number | null) => void;
  saveState?: FinancialSaveState;
  showHeaderValue?: boolean;
}

export const RevenueSection = ({
  figures,
  symbol,
  money,
  isReadOnly,
  isLoading = false,
  isLowestLevel = false,
  onForecastedRevenueChange,
  saveState = { status: 'idle' },
  showHeaderValue = false,
}: RevenueSectionProps) => {
  const { t } = useTranslation();
  const forecastedRevenue = figures?.forecastedRevenue ?? null;
  const [liveForecastedRevenue, setLiveForecastedRevenue] = useLiveDraft(forecastedRevenue);

  if (figures == null) {
    return null;
  }

  const below = figures.descendantsForecastRevenueAmount ?? null;
  const effectiveForecastRevenue = computeEffectiveAmount(
    isLowestLevel,
    liveForecastedRevenue,
    below,
  );

  return (
    <Section
      title={t(($) => $.financials.panel.revenueSectionTitle)}
      subtitle={
        isLowestLevel
          ? t(($) => $.financials.panel.revenueSectionSubtitleLeaf)
          : t(($) => $.financials.panel.revenueSectionSubtitleEffective)
      }
      headerValue={showHeaderValue ? money(effectiveForecastRevenue) : undefined}
    >
      <Row
        label={t(($) => $.financials.panel.forecastedRevenue)}
        right={
          isLoading ? (
            <Skeleton className="h-9 w-36" data-testid="financial-revenue-skeleton" />
          ) : isReadOnly || !onForecastedRevenueChange ? (
            money(figures.forecastedRevenue)
          ) : (
            <SavableUnitField
              saveState={saveState}
              value={forecastedRevenue}
              unit={symbol}
              unitSide="left"
              ariaLabel={t(($) => $.financials.panel.forecastedRevenue)}
              decimals={2}
              max={MAX_MONETARY_AMOUNT}
              maxDecimals={2}
              onCommit={onForecastedRevenueChange}
              onChange={setLiveForecastedRevenue}
              testId="financial-revenue-input"
            />
          )
        }
      />
      {below != null && !isLowestLevel && (
        <Row
          label={t(($) => $.financials.panel.descendantRevenue)}
          sublabel={t(($) => $.financials.panel.descendantRevenueSublabel)}
          right={money(below)}
        />
      )}
      <Row
        label={t(($) => $.financials.panel.revenueTotal)}
        sublabel={isLowestLevel ? undefined : t(($) => $.financials.panel.revenueTotalSublabel)}
        right={money(effectiveForecastRevenue)}
        variant={showHeaderValue ? 'totalHighlighted' : 'total'}
      />
    </Section>
  );
};
